import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { useGetContentItemQuery } from 'common/store/modules/api/content-api';
import { LazyImage } from 'common/react/components/LazyImage';
import { useBettingVis } from 'common/react/hooks/use-betting-vis';
import { SponsorSkeleton } from './SponsorSkeleton';
import { useSingleSiteSetting } from 'common/react/hooks/use-single-site-setting';

/**
 * Sponsor - component rendering sponsor PROMO
 *
 * @param {object} props props
 * @param {number} props.promoId ID of PROMO to fetch
 * @param {string} props.cssClass custom css class. Used only for the actual sponsor element
 * @param {string} props.skeletonCssClass custom css class. Used only for skeleton element (displayed when the PROMO is loading)
 * @param {object} props.promoPayload payload of PROMO to display. in case the PROMO is fetched outside of this component, pass the PROMO object. The promoId prop must be undefined for this to be applied.
 * @param {number} [props.imageHeight] Height of the PROMO image. Default 108 be undefined for this to be applied.
 * @returns {ReactElement} Sponsor element
 */
export const Sponsor = ({
    promoId,
    cssClass = '',
    skeletonCssClass = '',
    promoPayload,
    imageHeight = 108
}) => {
    const sponsorRef = useRef();
    const isBettingVisible = useBettingVis();
    const isOver18 = useSingleSiteSetting('over18');

    const { data: contentPromo, isFetching: isFetchingPromoData } =
        useGetContentItemQuery(
            {
                type: 'PROMO',
                id: promoId
            },
            { skip: !promoId }
        );

    const promo = promoPayload || contentPromo;

    const isBettingSponsor =
        PULSE.app.common.content.hasBettingSponsorTag(promo);
    const isAlcoholSponsor =
        PULSE.app.common.content.hasAlcoholSponsorTag(promo);
    const promoLink = promo?.links[0];
    const target = promoLink?.external ? '_blank' : null;

    useEffect(() => {
        if (sponsorRef.current) {
            PULSE.app.analytics.addSponsorTracking(sponsorRef.current);
        }
    }, [promo, sponsorRef]);

    if (
        (isBettingSponsor && !isBettingVisible) ||
        (isAlcoholSponsor && !isOver18)
    ) {
        return null;
    }

    return isFetchingPromoData ? (
        <SponsorSkeleton cssClass={skeletonCssClass} />
    ) : (
        <>
            {promo ? (
                <>
                    {/* <SponsorSkeleton cssClass={cssClass} /> */}
                    <div
                        className={`content-sponsor content-sponsor--without-borders content-sponsor--without-padding ${cssClass}`}
                        ref={sponsorRef}
                    >
                        {promo.title && promo.title !== '' ? (
                            <h2 className="content-sponsor__title">
                                {promo.title}
                            </h2>
                        ) : null}
                        {promoLink?.promoUrl ? (
                            <a
                                className="content-sponsor__image-link js-tracked-sponsor-item"
                                href={promoLink.promoUrl}
                                target={target}
                                title={promo.linkText || ''}
                                data-type={
                                    PULSE.app.common.CONSTANTS.TRACKING
                                        .CONTENT_TYPES.LINEUPS_SPONSOR
                                }
                                data-component-name={
                                    PULSE.app.common.CONSTANTS.TRACKING
                                        .CONTENT_TYPES.TEAM_LINEUP_CONDENSED
                                }
                                data-component-content-type={
                                    PULSE.app.common.CONSTANTS.TRACKING
                                        .CONTENT_TYPES.MATCH_INFO
                                }
                                data-link-text={`${promo.title || ''} ${
                                    promoLink.linkText || ''
                                }`}
                                data-name={promoLink.linkText || ''}
                            >
                                {promo?.promoItem?.onDemandUrl ? (
                                    <LazyImage
                                        url={`${promo.promoItem.onDemandUrl}?height=${imageHeight}`}
                                        cssClass="content-sponsor__image"
                                        objectFit={false}
                                    />
                                ) : null}
                                {promoLink.linkText ? (
                                    <span className="u-screen-reader">
                                        {promoLink.linkText}
                                    </span>
                                ) : null}
                            </a>
                        ) : (
                            <div className="content-sponsor__image-link">
                                {promo?.promoItem?.onDemandUrl ? (
                                    <LazyImage
                                        url={`${promo.promoItem.onDemandUrl}?height=${imageHeight}`}
                                        cssClass="content-sponsor__image"
                                        objectFit={false}
                                    />
                                ) : null}
                                {promoLink?.linkText ? (
                                    <span className="u-screen-reader">
                                        {promoLink.linkText}
                                    </span>
                                ) : null}
                            </div>
                        )}
                    </div>
                </>
            ) : null}
        </>
    );
};

Sponsor.propTypes = {
    promoId: PropTypes.string,
    cssClass: PropTypes.string,
    skeletonCssClass: PropTypes.string,
    promoPayload: PropTypes.object,
    imageHeight: PropTypes.number
};
